/**
 * @file utils.js
 * @brief Provides category admin utility helpers for slugging, pagination, flattening, and display labels.
 */
export function slugifyValue(value) {
    return value
        .toLowerCase()
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}


export async function fetchPaginated(api, path, params = {}) {
    const response = await api.get(path, { params });
    const payload = response.data;
    const items = Array.isArray(payload) ? payload : (payload.results ?? []);

    return {
        items,
        count: Array.isArray(payload) ? items.length : (payload.count ?? items.length),
        next: Array.isArray(payload) ? null : (payload.next ?? null),
        previous: Array.isArray(payload) ? null : (payload.previous ?? null),
    };
}


/**
 * Flatten the currently loaded category tree into a single ordered list.
 * @param {Array<Object>} roots Root categories loaded for the current view.
 * @param {Object} childrenByParent Loaded child nodes keyed by parent identifier.
 * @returns {Array<Object>} Flattened category list without duplicates.
 */
export function flattenLoadedCategories(roots, childrenByParent) {
    const flattened = [];
    const seen = new Set();

    /**
        * Walk a category subtree depth-first while preserving load order.
        * @param {Array<Object>} nodes Nodes to append to the flattened output.
        * @returns {void}
     */
    function walk(nodes) {
        nodes.forEach((node) => {
            if (seen.has(node.id)) {
                return;
            }
            seen.add(node.id);
            flattened.push(node);

            const children = childrenByParent[node.id]?.items || [];
            if (children.length > 0) {
                walk(children);
            }
        });
    }

    walk(roots);
    return flattened;
}


/**
 * Build a readable ancestry path for a category node.
 * @param {Object} node Category node whose ancestry should be displayed.
 * @param {Object} categoryById Loaded categories keyed by identifier.
 * @returns {string|null} Human-readable path, or null for root nodes.
 */
export function buildCategoryPath(node, categoryById) {
    if (!node.parent) {
        return null;
    }

    const names = [node.name];
    const visited = new Set([node.id]);
    let current = node;

    while (current.parent) {
        const parent = categoryById[current.parent];
        if (parent) {
            if (visited.has(parent.id)) {
                break;
            }
            names.push(parent.name);
            visited.add(parent.id);
            current = parent;
            continue;
        }

        if (current.parent_name) {
            names.push(current.parent_name);
        }
        break;
    }

    return names.reverse().join(" > ");
}


/**
 * Format a category option label for parent pickers.
 * @param {Object} category Category option or API payload.
 * @returns {string} Display label for the category option.
 */
export function formatCategoryOptionLabel(category) {
    return category.parent_name ? `${category.name} (${category.parent_name})` : category.name;
}
